(() => {
  if (window.__collectionLinkTileNavigationFixStarted) return;
  window.__collectionLinkTileNavigationFixStarted = true;

  const isPlainPrimaryClick = (event) =>
    event.button === 0
    && !event.metaKey
    && !event.ctrlKey
    && !event.shiftKey
    && !event.altKey;

  const getTileLink = (event) => {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!target) return null;

    const tile = target.closest('.section-main-collection-product-grid .collection-link-tile-item');
    if (!tile) return null;

    return tile.querySelector('a.collection-link-tile[href]');
  };

  window.addEventListener('click', (event) => {
    if (!isPlainPrimaryClick(event)) return;

    const link = getTileLink(event);
    if (!link) return;

    const rawHref = link.getAttribute('href');
    if (!rawHref || rawHref.trim() === '#') return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    window.location.assign(link.href);
  }, true);
})();

(() => {
  if (window.__collectionFacetsMetaobjectMobilePositionStarted) return;
  window.__collectionFacetsMetaobjectMobilePositionStarted = true;
  window.__collectionTileMobilePositionStarted = true;

  const mobileMedia = window.matchMedia('(max-width: 749px)');
  const gridSelector = '.section-main-collection-product-grid #product-grid';
  let updateScheduled = false;

  const normalizeText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  const normalizePath = (value) => {
    try {
      return new URL(value, window.location.origin).pathname.replace(/\/$/, '') || '/';
    } catch (error) {
      return String(value || '').replace(/\/$/, '');
    }
  };

  const readConfig = () => {
    const node = document.getElementById('collection-tile-position-config');
    if (!node) return [];

    try {
      const data = JSON.parse(node.textContent);
      return Array.isArray(data?.tiles) ? data.tiles : [];
    } catch (error) {
      console.warn('[collection tiles] Invalid position configuration.', error);
      return [];
    }
  };

  const isProductItem = (node) =>
    node instanceof Element
    && node.matches(
      '.grid__item.js-pagination__item:not(.collection-inline-promo):not(.collection-link-tile-item)'
    );

  const getProducts = (grid) => Array.from(grid.children).filter(isProductItem);

  const getTiles = (grid) => Array.from(grid.children).filter((child) =>
    child.classList.contains('collection-link-tile-item')
  );

  const removeTileAnimation = (tile) => {
    tile.classList.remove('scroll-trigger', 'animate--fade-in', 'scroll-trigger--offscreen');
    tile.removeAttribute('data-cascade');
    tile.style.removeProperty('--animation-order');
    tile.style.removeProperty('opacity');
    tile.style.removeProperty('transform');
    tile.style.removeProperty('order');
  };

  const clearOrderStyles = (grid) => {
    Array.from(grid.children).forEach((child) => child.style.removeProperty('order'));
  };

  const findNextProduct = (node) => {
    let sibling = node.nextSibling;
    while (sibling) {
      if (isProductItem(sibling)) return sibling;
      sibling = sibling.nextSibling;
    }
    return null;
  };

  const createState = (grid) => {
    const products = getProducts(grid);
    const tiles = getTiles(grid);
    if (!tiles.length) return null;

    return {
      mode: 'desktop',
      signature: '',
      entries: tiles.map((tile) => {
        removeTileAnimation(tile);
        const marker = document.createComment('collection-tile-desktop-position');
        grid.insertBefore(marker, tile);
        const nextProduct = findNextProduct(marker);

        return {
          tile,
          marker,
          originalProductIndex: nextProduct ? products.indexOf(nextProduct) + 1 : products.length + 1
        };
      })
    };
  };

  const stateIsCurrent = (grid, state) => {
    if (!state) return false;
    const currentTiles = getTiles(grid);
    if (currentTiles.length !== state.entries.length) return false;

    return state.entries.every(({ tile, marker }) =>
      tile.parentNode === grid
      && marker.parentNode === grid
      && currentTiles.includes(tile)
    );
  };

  const getState = (grid) => {
    let state = grid.__collectionTilePositionState;

    if (!stateIsCurrent(grid, state)) {
      state?.entries.forEach(({ marker }) => marker.parentNode === grid && marker.remove());
      state = createState(grid);
      grid.__collectionTilePositionState = state;
    }

    return state;
  };

  const restoreDesktop = (grid, state = getState(grid)) => {
    clearOrderStyles(grid);
    if (!state) return;

    state.entries.forEach(({ tile, marker }) => {
      removeTileAnimation(tile);
      if (
        tile.parentNode === grid
        && marker.parentNode === grid
        && marker.nextSibling !== tile
      ) {
        grid.insertBefore(tile, marker.nextSibling);
      }
    });

    state.mode = 'desktop';
    state.signature = '';
  };

  const getTileIdentity = (tile) => {
    const link = tile.querySelector('.collection-link-tile');
    return {
      path: normalizePath(link?.getAttribute('href') || ''),
      label: normalizeText(
        link?.getAttribute('aria-label')
        || tile.querySelector('.collection-link-tile__label')?.textContent
        || ''
      )
    };
  };

  const prepareConfig = (rawConfig) => {
    const config = rawConfig.map((item, index) => {
      const desktopPosition = Number.parseInt(item.desktopPosition, 10) || 0;
      const enteredMobilePosition = Number.parseInt(item.mobilePosition, 10) || 0;
      const mobilePosition = enteredMobilePosition > 0 ? enteredMobilePosition : desktopPosition;

      return {
        index,
        path: normalizePath(item.url),
        label: normalizeText(item.label),
        desktopPosition,
        mobilePosition,
        desktopRenderPosition: desktopPosition,
        mobileRenderPosition: mobilePosition
      };
    });

    for (let pass = 0; pass < config.length; pass += 1) {
      config.forEach((item) => {
        if (config.some((other) => other.desktopPosition === item.desktopRenderPosition - 1)) {
          item.desktopRenderPosition -= 1;
        }
        if (config.some((other) => other.mobilePosition === item.mobileRenderPosition - 1)) {
          item.mobileRenderPosition -= 1;
        }
      });
    }

    return config;
  };

  const matchEntries = (state, config) => {
    const used = new Set();

    return state.entries.map((entry) => {
      const identity = getTileIdentity(entry.tile);
      let index = config.findIndex((item, configIndex) =>
        !used.has(configIndex)
        && item.path === identity.path
        && item.label === identity.label
      );

      if (index < 0) {
        index = config.findIndex((item, configIndex) =>
          !used.has(configIndex) && item.path === identity.path
        );
      }

      if (index < 0) {
        index = config.findIndex((item, configIndex) =>
          !used.has(configIndex) && item.label === identity.label
        );
      }

      if (index < 0) {
        index = config.findIndex((item, configIndex) => !used.has(configIndex));
      }

      if (index >= 0) used.add(index);
      return { ...entry, config: index >= 0 ? config[index] : null };
    });
  };

  const getPageOffset = (assignments) => {
    const offsets = assignments
      .filter(({ config, originalProductIndex }) =>
        config
        && config.desktopRenderPosition > 0
        && originalProductIndex > 0
      )
      .map(({ config, originalProductIndex }) =>
        config.desktopRenderPosition - originalProductIndex
      )
      .filter((offset) => offset >= 0);

    if (!offsets.length) return 0;

    const counts = new Map();
    offsets.forEach((offset) => counts.set(offset, (counts.get(offset) || 0) + 1));
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || a[0] - b[0])[0][0];
  };

  const applyMobile = (grid, rawConfig) => {
    clearOrderStyles(grid);
    const state = getState(grid);
    if (!state) return;

    if (!rawConfig.length) {
      restoreDesktop(grid, state);
      return;
    }

    const config = prepareConfig(rawConfig);
    const assignments = matchEntries(state, config);
    const signature = assignments
      .map(({ config: item }) => item ? `${item.index}:${item.mobileRenderPosition}` : 'x')
      .join('|');

    if (state.mode === 'mobile' && state.signature === signature) return;
    if (state.mode === 'mobile') restoreDesktop(grid, state);

    const products = getProducts(grid);
    const pageOffset = getPageOffset(assignments);

    assignments
      .filter(({ config: item }) => item && item.mobileRenderPosition > 0)
      .sort((a, b) =>
        a.config.mobileRenderPosition - b.config.mobileRenderPosition
        || a.config.index - b.config.index
      )
      .forEach(({ tile, config: item }) => {
        const localProductPosition = item.mobileRenderPosition - pageOffset;
        const targetProduct = products[localProductPosition - 1];
        removeTileAnimation(tile);

        if (targetProduct?.parentNode === grid) {
          grid.insertBefore(tile, targetProduct);
        } else if (localProductPosition === products.length + 1) {
          grid.appendChild(tile);
        }
      });

    state.mode = 'mobile';
    state.signature = signature;
  };

  const sync = () => {
    const config = readConfig();
    document.querySelectorAll(gridSelector).forEach((grid) => {
      if (mobileMedia.matches) applyMobile(grid, config);
      else restoreDesktop(grid);
    });
  };

  const scheduleUpdate = () => {
    if (updateScheduled) return;
    updateScheduled = true;
    window.requestAnimationFrame(() => {
      updateScheduled = false;
      sync();
    });
  };

  const initialize = () => {
    scheduleUpdate();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  if (typeof mobileMedia.addEventListener === 'function') {
    mobileMedia.addEventListener('change', scheduleUpdate);
  } else {
    mobileMedia.addListener(scheduleUpdate);
  }

  document.addEventListener('shopify:section:load', scheduleUpdate);
  document.addEventListener('collection:products-updated', scheduleUpdate);
})();

(() => {
  if (window.__collectionFacetsSidebarToolbarStarted) return;
  window.__collectionFacetsSidebarToolbarStarted = true;

  const desktopMedia = window.matchMedia('(min-width: 990px)');
  const sectionSelector = '.section-main-collection-product-grid';
  let updateScheduled = false;
  let generatedId = 0;

  const getCurrentSortSelect = (section) => section.querySelector('#Vertical-SortBy');

  const syncSidebarSortState = (section, filter) => {
    const sourceSelect = getCurrentSortSelect(section);
    if (!sourceSelect || !filter) return;

    filter.querySelectorAll('[data-sidebar-sort-value]').forEach((input) => {
      const isActive = input.dataset.sidebarSortValue === sourceSelect.value;
      input.checked = isActive;
      input.nextElementSibling?.classList.toggle('active', isActive);
    });
  };

  const buildSortOptions = (section, filter, list, sourceSelect) => {
    const signature = Array.from(sourceSelect.options)
      .map((option) => `${option.value}\u0000${option.textContent.trim()}`)
      .join('\u0001');

    if (filter.dataset.optionsSignature === signature && list.children.length) {
      syncSidebarSortState(section, filter);
      return;
    }

    filter.dataset.optionsSignature = signature;
    list.replaceChildren();

    Array.from(sourceSelect.options).forEach((option, optionIndex) => {
      const item = document.createElement('li');
      item.className = 'list-menu__item facets__item';

      const input = document.createElement('input');
      input.type = 'radio';
      input.className = 'field__checkbox';
      input.id = `${filter.id}-Option-${optionIndex}`;
      input.value = option.value;
      input.dataset.sidebarSortValue = option.value;

      const label = document.createElement('label');
      label.className = 'facets__label facet-checkbox';
      label.htmlFor = input.id;

      const visibleText = document.createElement('span');
      visibleText.className = 'facet-checkbox__text';
      visibleText.setAttribute('aria-hidden', 'true');

      const visibleTextLabel = document.createElement('span');
      visibleTextLabel.className = 'facet-checkbox__text-label';
      visibleTextLabel.textContent = option.textContent.trim();

      const accessibleText = document.createElement('span');
      accessibleText.className = 'visually-hidden';
      accessibleText.textContent = option.textContent.trim();

      visibleText.appendChild(visibleTextLabel);
      label.append(visibleText, accessibleText);
      item.append(input, label);
      list.appendChild(item);

      input.addEventListener('change', () => {
        if (!input.checked) return;

        const currentSourceSelect = getCurrentSortSelect(section);
        if (!currentSourceSelect || currentSourceSelect.value === input.value) return;

        currentSourceSelect.value = input.value;
        currentSourceSelect.dispatchEvent(new Event('change', { bubbles: true }));
        syncSidebarSortState(section, filter);
      });
    });

    syncSidebarSortState(section, filter);
  };

  const createSidebarSortFilter = (section, wrapper, sourceSelect) => {
    const firstFilter = wrapper.querySelector(':scope > custom-accordion');
    const filter = document.createElement('div');
    generatedId += 1;
    filter.id = `SidebarSortDetails-${generatedId}`;
    filter.className = 'js-filter sidebar-sort-filter';

    const details = document.createElement('details');
    details.className = 'facets__disclosure-vertical';
    details.dataset.index = 'SidebarSort';
    if (firstFilter?.querySelector('details[open]')) details.open = true;

    const summary = document.createElement('summary');
    summary.className = 'facets__summary caption-large focus-inset no-background-blur';
    summary.setAttribute('aria-label', 'Sortowanie');

    const summaryContent = document.createElement('div');
    const summaryLabel = document.createElement('span');
    summaryLabel.className = 'facets__summary-label';
    summaryLabel.textContent = 'Sortowanie';
    summaryContent.appendChild(summaryLabel);

    const caret = firstFilter?.querySelector('summary .icon-caret')?.cloneNode(true)
      || section.querySelector('.icon-caret')?.cloneNode(true);
    if (caret) summaryContent.appendChild(caret);
    summary.appendChild(summaryContent);

    const panel = document.createElement('div');
    panel.className = 'custom-accordion__panel custom-scrollbar parent-display facets__display-vertical sidebar-sort-filter__display';

    const fieldset = document.createElement('fieldset');
    fieldset.className = 'facets-wrap parent-wrap facets-wrap-vertical';

    const legend = document.createElement('legend');
    legend.className = 'visually-hidden';
    legend.textContent = 'Sortowanie';

    const list = document.createElement('ul');
    list.className = 'facets-layout facets-layout-list facets-layout-list--text facets__list--vertical list-unstyled no-js-hidden sidebar-sort-filter__list';
    list.setAttribute('role', 'radiogroup');
    list.setAttribute('aria-label', 'Sortowanie produktów');

    fieldset.append(legend, list);
    panel.appendChild(fieldset);
    details.append(summary, panel);
    filter.appendChild(details);
    wrapper.insertBefore(filter, firstFilter || wrapper.firstChild);
    buildSortOptions(section, filter, list, sourceSelect);
    return filter;
  };

  const ensureSidebarSortFilter = (section, sidebar, sourceSelect) => {
    const wrapper = sidebar.querySelector('#FacetsWrapperDesktop');
    if (!wrapper || !sourceSelect) return;

    let filter = wrapper.querySelector(':scope > .sidebar-sort-filter');
    if (!filter) {
      filter = createSidebarSortFilter(section, wrapper, sourceSelect);
    } else {
      const list = filter.querySelector('.sidebar-sort-filter__list');
      if (list) buildSortOptions(section, filter, list, sourceSelect);
    }
  };

  const syncToolbarPosition = () => {
    document.querySelectorAll(sectionSelector).forEach((section) => {
      const sidebar = section.querySelector('#main-collection-filters');
      const productGridContainer = section.querySelector('#ProductGridContainer');
      if (!sidebar || !productGridContainer) return;

      const sidebarToolbar = sidebar.querySelector(':scope > .facets-vertical-sort');
      const gridToolbar = productGridContainer.querySelector('.facets-vertical-sort');

      if (desktopMedia.matches) {
        if (gridToolbar && sidebarToolbar && sidebarToolbar !== gridToolbar) sidebarToolbar.remove();

        const toolbar = gridToolbar || sidebarToolbar;
        if (!toolbar) return;

        if (toolbar.parentElement !== sidebar || toolbar !== sidebar.firstElementChild) sidebar.prepend(toolbar);
        ensureSidebarSortFilter(section, sidebar, toolbar.querySelector('#Vertical-SortBy'));
        return;
      }

      section.querySelectorAll('.sidebar-sort-filter').forEach((filter) => filter.remove());
      if (!sidebarToolbar) return;

      const activeFacets = productGridContainer.querySelector('.active-facets--vertical');
      const productCollection = productGridContainer.querySelector('#product-grid')?.closest('.collection');
      const returnTarget = activeFacets || productCollection;

      if (returnTarget?.parentElement) returnTarget.parentElement.insertBefore(sidebarToolbar, returnTarget);
      else productGridContainer.prepend(sidebarToolbar);
    });
  };

  const scheduleToolbarUpdate = () => {
    if (updateScheduled) return;
    updateScheduled = true;
    window.requestAnimationFrame(() => {
      updateScheduled = false;
      syncToolbarPosition();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleToolbarUpdate, { once: true });
  } else {
    scheduleToolbarUpdate();
  }

  if (typeof desktopMedia.addEventListener === 'function') desktopMedia.addEventListener('change', scheduleToolbarUpdate);
  else desktopMedia.addListener(scheduleToolbarUpdate);

  document.addEventListener('shopify:section:load', scheduleToolbarUpdate);
  document.addEventListener('collection:products-updated', scheduleToolbarUpdate);
})();

(() => {
  if (window.__collectionDescriptionSetupStarted) return;
  window.__collectionDescriptionSetupStarted = true;

  let descriptionIndex = 0;

  const findCollectionDescriptions = () => {
    const descriptions = Array.from(document.querySelectorAll(
      '.section-main-collection-banner .content-truncator__content.collection__text, .section-main-collection-banner .collection__text'
    ));
    const productGrid = document.querySelector('.section-main-collection-product-grid');
    if (!productGrid) return descriptions;

    const candidates = Array.from(document.querySelectorAll('.section-rich-text .rich-text__text')).filter((element) => {
      const position = element.compareDocumentPosition(productGrid);
      return Boolean(position & Node.DOCUMENT_POSITION_FOLLOWING) && element.textContent.trim().length > 120;
    });

    const richTextDescription = candidates.find((element) =>
      Array.from(element.children).some((child) => /^H[1-6]$/.test(child.tagName))
    ) || candidates[candidates.length - 1];

    if (richTextDescription) descriptions.push(richTextDescription);
    return Array.from(new Set(descriptions));
  };

  const prepareDescriptionBody = (description) => {
    const existingBody = description.querySelector(':scope > .collection-description-body');
    if (existingBody) return existingBody;

    const directHeading = Array.from(description.children).find((child) => /^H[1-6]$/.test(child.tagName));
    const body = document.createElement('div');
    body.className = 'collection-description-body';

    if (directHeading) {
      let moveFollowingNodes = false;
      Array.from(description.childNodes).forEach((node) => {
        if (node === directHeading) {
          moveFollowingNodes = true;
        } else if (moveFollowingNodes) {
          body.appendChild(node);
        }
      });
    } else {
      while (description.firstChild) body.appendChild(description.firstChild);
    }

    if (!body.childNodes.length) return null;
    description.appendChild(body);
    return body;
  };

  const setupCollectionDescription = () => {
    findCollectionDescriptions().forEach((description) => {
      if (description.dataset.collectionCroReady === 'true') return;

      const body = prepareDescriptionBody(description);
      if (!body) return;

      description.dataset.collectionCroReady = 'true';
      descriptionIndex += 1;
      if (!body.id) body.id = `CollectionDescriptionBody-${descriptionIndex}`;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'collection-description-toggle';
      button.textContent = 'Czytaj więcej';
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', body.id);
      button.hidden = true;
      description.insertAdjacentElement('afterend', button);

      const updateButton = () => {
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        const hasLongContent = body.textContent.trim().length > 120 || body.scrollHeight > 72;

        if (!isMobile || !hasLongContent) {
          body.classList.remove('is-expanded');
          button.textContent = 'Czytaj więcej';
          button.setAttribute('aria-expanded', 'false');
          button.hidden = true;
          return;
        }

        button.hidden = false;
      };

      button.addEventListener('click', () => {
        const expanded = body.classList.toggle('is-expanded');
        button.textContent = expanded ? 'Czytaj mniej' : 'Czytaj więcej';
        button.setAttribute('aria-expanded', String(expanded));
      });

      updateButton();
      window.addEventListener('resize', updateButton, { passive: true });
      document.fonts?.ready?.then(updateButton).catch(() => {});
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupCollectionDescription, { once: true });
  } else {
    setupCollectionDescription();
  }

  document.addEventListener('shopify:section:load', setupCollectionDescription);
})();
